import { FunctionComponent, useState } from 'react';
import { ErrorMessage, Field, FieldProps } from 'formik';
import { Listbox, ListboxButton, ListboxOptions } from '@headlessui/react';
import { oneLine } from 'common-tags';

import { CaretDown, CaretUp } from '@/icons';

import { TypeSelectProps } from './types';
import { TypeOption } from './childs/types';

const DEFAULT = 'text-text-secondary border-secondary-600 ring-secondary-600';
const ERROR = 'text-tertiary-500 border-tertiary-500 ring-tertiary-500';


export const Select: FunctionComponent<TypeSelectProps> = ({
  className = '',
  name,
  label,
  disabled = false,
  children,
  placeholder = '',
  padding = '',
}: TypeSelectProps) => {
  const [selected, setSelected] = useState(placeholder);

  return (
    <Field name={name}>
      {({
        meta: { error, touched },
        field: { value },
        form: { setFieldValue, setFieldTouched },
      }: FieldProps) => (
        <div
          className={oneLine`
            aria-disabled:opacity-40 flex flex-col gap-2
            ${error && touched ? ERROR : DEFAULT}
            ${className}
          `}
          aria-disabled={disabled}
        >
          {label && (
            <label htmlFor={name} className={`text-body text-body--m ${error && touched ? 'text-tertiary-500' : 'text-text-secondary'}`}>
              {label}
            </label>
          )}

          <Listbox
            disabled={disabled}
            onChange={(e: TypeOption) => {
              setFieldTouched(name, true, true);
              setFieldValue(name, e.value);
              setSelected(e.label);
            }}
          >
            {({ open }) => {
              const Icon = open ? CaretUp : CaretDown;

              return (
                <div className="text-body text-body--l">
                  <ListboxButton
                    className={oneLine`
                      relative w-full ${padding ? padding : "px-4 py-3"} border
                  ${open ? 'border-b-0 border-primary-500' : ''}
                  ${value !== '' && !(error && touched)
                        ? 'text-text-primary border-secondary-600'
                        : ''
                      }
                  `}
                  >
                    <span className="block truncate text-left">{selected}</span>

                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <Icon className="h-6 w-6" aria-hidden="true" />
                    </span>
                  </ListboxButton>

                  <ListboxOptions
                    transition
                    unmount={false}
                    className="absolute w-[calc(100%-2rem)] md:w-[calc(100%-7rem)] lg:w-[calc(50%-3.5rem-8px)] overflow-auto border border-primary-500 divide-y divide-primary-500 text-text-primary z-10 transition duration-100 ease-in data-[closed]:opacity-0"
                  >
                    {children}
                  </ListboxOptions>
                </div>
              );
            }}
          </Listbox>

          <ErrorMessage name={name}>
            {(msg) => <div className="text-body text-body--s">{msg}</div>}
          </ErrorMessage>
        </div>
      )}
    </Field>
  );
};
Select.displayName = 'Select';
